/* ---------------------------------------------------------------------------
   PK — header cart icon guard

   The active header on this store is the Section Store section "SS - Header #13",
   whose cart link is <a id="cart-icon-bubble"> containing its own icon span,
   text span and .cart-count-bubble.

   Dawn's cart JS assumes that id belongs to Dawn's own header and replaces the
   WHOLE contents of the link with the rendered `cart-icon-bubble` section. The
   Section Store header has no CSS for that markup, so the injected SVG renders
   at 0x0 and the cart icon vanishes until the next page load.

   This guard:
     1. exposes pkHeaderCart.setCount() so the count can be refreshed without
        touching the surrounding markup, and
     2. snapshots the original markup on load and restores it if anything (theme
        code or an app) wipes it — so the icon can never disappear.
--------------------------------------------------------------------------- */
(function () {
  var snapshot = null;
  var countPrefix = '';
  var countSuffix = '';
  var observer = null;

  function cartLink() {
    return document.getElementById('cart-icon-bubble');
  }

  function markupIsIntact(el) {
    if (!el) return true;
    if (!el.innerHTML.trim()) return false;
    // The Section Store header markup always carries these class prefixes.
    return !!el.querySelector('[class*="header-cart-icon-"], [class*="header-cart-text-"]');
  }

  function readCount(el) {
    if (!el) return null;
    var match = el.textContent.replace(/\s+/g, '').match(/(\d+)/);
    return match ? parseInt(match[1], 10) : null;
  }

  function setCount(count) {
    var el = cartLink();
    if (!el) return;
    var value = parseInt(count, 10);
    if (isNaN(value)) return;

    var bubble = el.querySelector('.cart-count-bubble');
    if (!bubble) {
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      el.appendChild(bubble);
    }
    var label = value < 100 ? String(value) : '99+';
    bubble.innerHTML = '<span aria-hidden="true">' + countPrefix + label + countSuffix + '</span>';
  }

  function restore() {
    var el = cartLink();
    if (!el || snapshot === null || markupIsIntact(el)) return;
    var count = readCount(el);
    if (observer) observer.disconnect();
    el.innerHTML = snapshot;
    if (count !== null) setCount(count);
    watch(el);
  }

  function watch(el) {
    if (!el || typeof MutationObserver === 'undefined') return;
    observer = new MutationObserver(restore);
    observer.observe(el, { childList: true, subtree: true });
  }

  function refresh() {
    if (!window.routes || !window.routes.cart_url) return;
    fetch(window.routes.cart_url + '.js')
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        setCount(cart.item_count);
      })
      .catch(function () {});
  }

  function init() {
    var el = cartLink();
    if (!el) return;
    if (observer) observer.disconnect();
    snapshot = el.innerHTML;
    var span = el.querySelector('.cart-count-bubble span');
    if (span) {
      var parts = span.textContent.trim().match(/^(\D*)\d+(\D*)$/);
      if (parts) {
        countPrefix = parts[1];
        countSuffix = parts[2];
      }
    }
    watch(el);
  }

  window.pkHeaderCart = { setCount: setCount, refresh: refresh, restore: restore, init: init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', init);
})();

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
  }

  connectedCallback() {
    // connectedCallback is the safe place for DOM access in custom elements.
    // Moved from constructor to avoid null-reference errors during element upgrade.
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));

    // Only wire up the cart icon once, even if the element is reconnected.
    if (!this._cartIconSetup) {
      this.setHeaderCartIconAccessibility();
      this._cartIconSetup = true;
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement) return;
      const sectionHTML = parsedState.sections[section.id];
      if (!sectionHTML) return;
      const innerContent = this.getSectionInnerHTML(sectionHTML, section.selector);
      if (innerContent !== null) sectionElement.innerHTML = innerContent;
    });

    // Update the header cart count without touching the header's own markup.
    this.updateCartCount(parsedState.item_count);

    // Hide any error-state SVGs that have no accompanying error message
    document.querySelectorAll('.cart-item__error').forEach((errorEl) => {
      const text = errorEl.querySelector('.cart-item__error-text');
      const svg = errorEl.querySelector('svg');
      if (svg && (!text || !text.textContent.trim())) {
        svg.style.display = 'none';
      }
    });

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return null;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const element = doc.querySelector(selector);
    return element ? element.innerHTML : null;
  }

  updateCartCount(count) {
    if (!window.pkHeaderCart) return;
    // /cart/add.js responses do not include item_count, so fall back to /cart.js.
    if (typeof count === 'number' && !isNaN(count)) {
      window.pkHeaderCart.setCount(count);
    } else {
      window.pkHeaderCart.refresh();
    }
  }

  getSectionsToRender() {
    // NOTE: `cart-icon-bubble` is deliberately NOT rendered here. Replacing the
    // contents of #cart-icon-bubble destroys the Section Store header's own cart
    // markup and the icon disappears. The count is updated by updateCartCount().
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    // See the note in CartDrawer.getSectionsToRender — the header cart icon is
    // updated by count only, never re-rendered.
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
